import { sendPayment, mapError, SupportedNetwork } from './sendPayment';
import { isValidNetwork } from '../config/tokens';
import { API_CONFIG } from '../config/constants';

type RadomPaymentIntent = {
  payment_id: string;
  session_id: string;
  network: SupportedNetwork;
  wallet_address: string;   // where to send
  amount: string;           // human string ("100", "0.015")
  
  // Prefer these from backend (safer than inferring from symbol)
  token_address?: string | null;
  token_decimals?: number | null;
  
  // Optional: for pull/transferFrom models
  spender?: string | null;
  intent?: 'transfer' | 'transferFrom';
  
  // Optional: only if you insist on symbols (see safety note below)
  token_symbol?: 'ETH' | 'BNB' | 'MATIC' | 'AVAX' | 'USDT' | 'USDC';
  
  // Session info
  expires_at: string;
  tokens_to_receive: number;
};

interface PaymentData {
  amount: string;
  currency: string; // e.g., "USDT" or "ETH"
  walletAddress: string;
  network: string;
}

interface PaymentCallbacks {
  onSwitching?: () => void;
  onAwaitingApproval?: () => void;
  onAwaitingSignature?: () => void;
  onBroadcasting?: () => void;
  onSuccess?: (hash: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Handle complete Radom wallet payment flow
 * 1. Create Radom payment intent via backend
 * 2. Send transaction via wallet
 * 3. Notify backend of transaction hash
 */
export async function handleRadomWalletPayment(
  paymentData: PaymentData,
  callbacks: PaymentCallbacks = {}
): Promise<{ success: true; hash: string; session_id: string }> {
  
  const {
    onSwitching,
    onAwaitingApproval,
    onAwaitingSignature,
    onBroadcasting,
    onSuccess,
    onError
  } = callbacks;

  try {
    // 1) Ask backend to create a Radom payment + return a payment intent
    console.log('🔄 Creating Radom payment intent:', paymentData);
    
    const res = await fetch(`${API_CONFIG.BASE_URL}/api/payment/create-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: paymentData.walletAddress,
        amount_usd: parseFloat(paymentData.amount),
        network: paymentData.network,
        token: paymentData.currency === 'ETH' || paymentData.currency === 'BNB' || 
               paymentData.currency === 'MATIC' || paymentData.currency === 'AVAX' 
               ? undefined : paymentData.currency
      })
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `Radom create failed (${res.status})`);
    }

    const intent: RadomPaymentIntent = await res.json();
    console.log('✅ Radom payment intent created:', intent);

    // Basic validation (prevents footguns)
    if (!intent.wallet_address || !intent.amount || !intent.network) {
      throw new Error('Incomplete Radom payload: missing wallet_address/amount/network');
    }

    // 2) Build token args (prefer backend-provided address/decimals)
    const tokenArgs = intent.token_address ? {
      address: intent.token_address,
      decimals: intent.token_decimals ?? undefined,
      spender: intent.spender ?? undefined,
      intent: intent.intent ?? 'transfer',
    } : undefined;

    console.log('🚀 Sending payment via wallet:', {
      network: intent.network,
      to: intent.wallet_address,
      amount: intent.amount,
      token: tokenArgs
    });

    // 3) Fire the wallet confirmation (works with all EVM wallets)
    const { hash } = await sendPayment(
      {
        network: intent.network,
        to: intent.wallet_address,
        amount: intent.amount,
        token: tokenArgs,
      },
      {
        onSwitching: () => {
          console.debug('[radom] switching network…');
          onSwitching?.();
        },
        onAwaitingApproval: () => {
          console.debug('[radom] awaiting token approval…');
          onAwaitingApproval?.();
        },
        onAwaitingSignature: () => {
          console.debug('[radom] awaiting signature…');
          onAwaitingSignature?.();
        },
        onBroadcasting: () => {
          console.debug('[radom] broadcasting…');
          onBroadcasting?.();
        },
      }
    );

    console.log('✅ Transaction sent:', hash);

    // 4) Tell backend which tx hash to associate with this Radom payment
    try {
      await fetch(`${API_CONFIG.BASE_URL}/api/payment/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          payment_id: intent.payment_id,
          session_id: intent.session_id,
          tx_hash: hash 
        }),
      });
      console.log('✅ Backend notified of transaction hash');
    } catch (ackError) {
      console.warn('⚠️ Failed to notify backend, but transaction was sent:', ackError);
      // Don't throw here - the payment was successful, just acknowledgment failed
    }

    onSuccess?.(hash);
    return { 
      success: true, 
      hash, 
      session_id: intent.session_id 
    };

  } catch (error: any) {
    console.error('❌ Radom wallet payment failed:', error);
    
    const mappedError = mapError(error);
    onError?.(mappedError);
    
    // Bubble up a clean message; UI can switch to QR fallback if desired
    throw mappedError;
  }
}

/**
 * Check if a network/token combination supports direct wallet payments
 */
export function supportsDirectWalletPayment(network: string, token?: string): boolean {
  if (!isValidNetwork(network)) {
    return false;
  }

  // All native tokens are supported
  const nativeTokens = {
    ethereum: 'ETH',
    arbitrum: 'ETH', 
    base: 'ETH',
    polygon: 'MATIC',
    bsc: 'BNB',
    avalanche: 'AVAX'
  };

  if (!token || token.toUpperCase() === nativeTokens[network as keyof typeof nativeTokens]) {
    return true;
  }

  // Common ERC-20 tokens are supported (backend will provide addresses)
  const supportedTokens = ['USDT', 'USDC', 'DAI', 'WETH', 'WBTC'];
  return supportedTokens.includes(token.toUpperCase());
}

export type { RadomPaymentIntent, PaymentData, PaymentCallbacks };